import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import { useTranslation } from "react-i18next";
import { ISimCardChangeInfo, IStreetsResponse, IUserAddress } from "./Interface/IAddressForm";
import { UseFormMethods, useWatch } from "react-hook-form";
import { useEffect, useState } from "react";
import { useFetch } from "@common/hook/useFetch";
import { findAddress } from "@api-routes";
import { Button } from "@material-ui/core";
import { PostalBoxSwitch } from "./PostalBoxSwitch";
import { StreetsDropdown } from "./StreetsDropdown";
import { BuildingTypeDropdown } from "./BuildingTypeDropdown";
import UploadFileButton from "@common/uploadFileButton/UploadFileButton";

interface IParams {
    addressForm: (UseFormMethods<IUserAddress> | UseFormMethods<ISimCardChangeInfo>) & Partial<UseFormMethods>;
    isEditing: boolean;
    isLoading: boolean;
    prefix?: string;
}

export const Address = (props: IParams) => {
    const { isLoading, addressForm, isEditing, prefix } = props;
    const { errors, register, control } = addressForm;
    const [streets, setStreets] = useState<IStreetsResponse | undefined>();
    const [_, { get }] = useFetch();
    const namePrefix = prefix ? `${prefix}.` : "";
    const idPrefix = prefix ? `${prefix}-` : "";
    const { t } = useTranslation(["address", "validation", "common"]);
    const isFieldDisabled = !isEditing || isLoading;
    const city = useWatch<string>({ name: `${namePrefix}city`, control: addressForm.control });
    const area = useWatch<string>({ name: `${namePrefix}area`, control: addressForm.control });
    const street = useWatch<string>({ name: `${namePrefix}street`, control: addressForm.control });
    const postalCode = useWatch<string>({ name: `${namePrefix}postalCode`, control: addressForm.control });
    const buildingType = useWatch<string>({ name: `${namePrefix}buildingType`, control: addressForm.control });
    const buildingName = useWatch<string>({ name: `${namePrefix}buildingName`, control: addressForm.control });
    const streetQualifier = useWatch<string>({ name: `${namePrefix}streetQualifier`, control: addressForm.control });
    const streetNumber = useWatch<string>({ name: `${namePrefix}streetNumber`, control: addressForm.control });
    const flatNumber = useWatch<string>({ name: `${namePrefix}flatNumber`, control: addressForm.control });
    const enablePostalBox = useWatch<boolean>({ name: `${namePrefix}enablePostalBox`, control: addressForm.control });
    const postalBox = useWatch<boolean>({ name: `${namePrefix}postalBox`, control: addressForm.control });

    useEffect(() => {
        if (street) {
            onPostalConfirmClick(false);
        }
    }, []);

    const onPostalConfirmClick = (resetValues: boolean) => {
        const postalValue = postalCode || "";
        get<IStreetsResponse>(findAddress(postalValue), { isSecured: false }).then((streetsResponse) => {
            if (streetsResponse) {
                setStreets(streetsResponse);
                if (resetValues) {
                    addressForm.setValue(`${namePrefix}street`, undefined);
                    addressForm.setValue(`${namePrefix}streetNumber`, undefined);
                    addressForm.setValue(`${namePrefix}buildingType`, undefined);
                    addressForm.setValue(`${namePrefix}buildingName`, undefined);
                    addressForm.setValue(`${namePrefix}streetQualifier`, undefined);
                    addressForm.setValue(`${namePrefix}flatNumber`, undefined);
                }
                addressForm.setValue(`${namePrefix}city`, streetsResponse.city);
                addressForm.setValue(`${namePrefix}area`, streetsResponse.area);
            }
        });
    };

    return (
        <Grid container justify="space-between" spacing={2}>
            <Grid container direction="row" alignItems="center" spacing={2}>
                <Grid item md={5} xs={12}>
                    <TextField
                        InputLabelProps={{ shrink: Boolean(postalCode) }}
                        fullWidth
                        id={`${idPrefix}postalCode`}
                        inputRef={register({
                            required: t("validation:required"),
                            pattern: {
                                value: /^([\d]{4})$/,
                                message: t("address:postCodeError"),
                            },
                        })}
                        label={`${t("address:postalCode")} *`}
                        margin="dense"
                        name={`${namePrefix}postalCode`}
                        type="text"
                        error={Boolean(errors.billingAddress?.postalCode)}
                        helperText={errors.billingAddress?.postalCode?.message}
                        disabled={isFieldDisabled}
                    />
                </Grid>

                <Grid item md={5} xs={12}>
                    <Button
                        disabled={!postalCode || postalCode.length < 4 || isFieldDisabled ? true : false}
                        color={"primary"}
                        id={`${idPrefix}postalConfirmButton`}
                        type="button"
                        variant="contained"
                        onClick={() => onPostalConfirmClick(true)}
                    >
                        {t("address:find")}
                    </Button>
                </Grid>
            </Grid>
            <Grid item xs={12}>
                <PostalBoxSwitch control={control} prefix={prefix} disabled={isFieldDisabled} />
            </Grid>

            {!enablePostalBox && (
                <Grid item xs={12}>
                    <StreetsDropdown
                        streets={streets}
                        error={errors.billingAddress?.street}
                        control={control}
                        prefix={prefix}
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}

            <Grid item xs={12}>
                <TextField
                    disabled
                    InputLabelProps={{ shrink: Boolean(city) }}
                    fullWidth
                    id={`${idPrefix}city`}
                    label={`${t("address:city")} *`}
                    inputRef={register({})}
                    margin="dense"
                    name={`${namePrefix}city`}
                    type="text"
                />
            </Grid>

            <Grid item xs={12}>
                <TextField
                    disabled
                    InputLabelProps={{ shrink: Boolean(area) }}
                    fullWidth
                    id={`${idPrefix}area`}
                    inputRef={register({})}
                    label={`${t("address:area")} *`}
                    margin="dense"
                    name={`${namePrefix}area`}
                    type="text"
                />
            </Grid>

            {!enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        error={Boolean(errors.billingAddress?.streetNumber)}
                        helperText={errors.billingAddress?.streetNumber?.message}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(streetNumber) }}
                        id={`${idPrefix}streetNumber`}
                        inputRef={register({
                            required: t("validation:required"),
                        })}
                        label={`${t("address:streetNumber")} *`}
                        margin="dense"
                        name={`${namePrefix}streetNumber`}
                        type="text"
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}
            {!enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        style={{ display: "none" }}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(streetQualifier) }}
                        id={`${idPrefix}streetQualifier`}
                        label={`${t("address:streetQualifier")}`}
                        margin="dense"
                        inputRef={register({})}
                        name={`${namePrefix}streetQualifier`}
                        type="text"
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}

            {!enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <BuildingTypeDropdown
                        error={errors.billingAddress?.buildingType}
                        control={control}
                        prefix={prefix}
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}

            {!enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(buildingName) }}
                        id={`${idPrefix}buildingName`}
                        label={`${t("address:buildingName")}`}
                        margin="dense"
                        name={`${namePrefix}buildingName`}
                        type="text"
                        inputRef={register({})}
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}

            {addressForm.getValues(`${namePrefix}buildingType`) === "BLOCK_OF_FLATS" && !enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        fullWidth
                        error={Boolean(errors.billingAddress?.flatNumber)}
                        helperText={errors.billingAddress?.flatNumber?.message}
                        InputLabelProps={{ shrink: Boolean(flatNumber) }}
                        id={`${idPrefix}flatNumber`}
                        label={`${t("address:flatNumber")}`}
                        margin="dense"
                        name={`${namePrefix}flatNumber`}
                        type="text"
                        inputRef={register({
                            required: t("validation:required"),
                        })}
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}

            {enablePostalBox && (
                <Grid item sm={5} xs={12}>
                    <TextField
                        error={Boolean(errors.billingAddress?.postalBox)}
                        helperText={errors.billingAddress?.postalBox?.message}
                        fullWidth
                        InputLabelProps={{ shrink: Boolean(postalBox) }}
                        id={`${idPrefix}postalBox`}
                        inputRef={register({
                            required: t("validation:required"),
                            pattern: {
                                value: /^([\d]{5})$/,
                                message: t("address:postalBoxError"),
                            },
                        })}
                        label={`${t("address:postalBox")} *`}
                        margin="dense"
                        name={`${namePrefix}postalBox`}
                        type="text"
                        disabled={isFieldDisabled}
                    />
                </Grid>
            )}
            {isEditing && (
                <Grid item xs={12}>
                    <UploadFileButton
                        error={errors.billingAddress?.proofOfAddress}
                        control={control}
                        prefix={prefix}
                        disabled={isFieldDisabled}
                        name={"proofOfAddress"}
                        buttonText={`${t("address:uploadFile")} *`}
                        preventCentering={true}
                    />
                </Grid>
            )}
        </Grid>
    );
};
